import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faTimes } from '@fortawesome/free-solid-svg-icons';
import './Select.css';
import './MultiSelect.css';
import { SelectOption } from './Select';

interface MultiSelectProps {
    values: string[];
    onChange: (values: string[]) => void;
    options: SelectOption[];
    placeholder?: string;
    disabled?: boolean;
    label?: string;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
    values,
    onChange,
    options,
    placeholder = 'Selecione',
    disabled = false,
    label,
}) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputContainerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
    const [searchTerm, setSearchTerm] = useState('');

    const updateDropdownPosition = useCallback(() => {
        if (inputContainerRef.current) {
            const rect = inputContainerRef.current.getBoundingClientRect();
            const isMobile = window.innerWidth <= 768;
            const dropdownWidth = rect.width;
            const viewportWidth = window.innerWidth;
            let leftPosition = rect.left;
            if (leftPosition + dropdownWidth > viewportWidth) {
                leftPosition = Math.max(16, viewportWidth - dropdownWidth - 16);
            }
            setDropdownStyle({
                position: 'fixed',
                top: rect.bottom + 4,
                left: leftPosition,
                width: rect.width,
                maxWidth: isMobile ? 'calc(100vw - 2rem)' : 'none',
                zIndex: 2000,
            });
        }
    }, []);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            if (
                wrapperRef.current && !wrapperRef.current.contains(target) &&
                dropdownRef.current && !dropdownRef.current.contains(target)
            ) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };
        const handleResize = () => { if (isOpen) updateDropdownPosition(); };
        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleResize, true);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleResize, true);
        };
    }, [isOpen, updateDropdownPosition]);

    const toggleDropdown = () => {
        if (!disabled) {
            if (!isOpen) updateDropdownPosition();
            setIsOpen(prev => !prev);
            setSearchTerm('');
        }
    };

    const handleToggle = (optionValue: string) => {
        if (optionValue === '') {
            onChange([]);
        } else {
            const next = values.includes(optionValue)
                ? values.filter(v => v !== optionValue)
                : [...values, optionValue];
            onChange(next);
        }
    };

    const removeTag = (e: React.MouseEvent, optionValue: string) => {
        e.stopPropagation();
        onChange(values.filter(v => v !== optionValue));
    };

    const selectedOptions = options.filter(o => values.includes(o.value));
    const todosOption = options.find(o => o.value === '');
    const nonTodosOptions = options.filter(o => o.value !== '');
    const filteredOptions = nonTodosOptions.filter(o =>
        o.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const MAX_VISIBLE_TAGS = 2;
    const visibleOptions = selectedOptions.slice(0, MAX_VISIBLE_TAGS);
    const hiddenCount = selectedOptions.length - MAX_VISIBLE_TAGS;

    const displayContent = values.length === 0 ? (
        <span className="select-input placeholder">{placeholder}</span>
    ) : (
        <div className="multiselect-tags">
            {visibleOptions.map(opt => (
                <span key={opt.value} className="multiselect-tag" title={opt.label}>
                    <span className="multiselect-tag-label">{opt.label}</span>
                    <button
                        className="multiselect-tag-remove"
                        onClick={(e) => removeTag(e, opt.value)}
                        tabIndex={-1}
                        aria-label={`Remover ${opt.label}`}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </span>
            ))}
            {hiddenCount > 0 && (
                <span className="multiselect-more-badge" title={selectedOptions.slice(MAX_VISIBLE_TAGS).map(o => o.label).join(', ')}>
                    +{hiddenCount}
                </span>
            )}
        </div>
    );

    const dropdown = isOpen ? (
        <div className="select-dropdown" style={dropdownStyle} ref={dropdownRef}>
            <div className="select-search">
                <input
                    type="text"
                    className="select-search-input"
                    placeholder="Pesquisar..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    autoFocus
                />
            </div>
            <div className="select-options">
                {todosOption && (
                    <div
                        className={`select-option multiselect-option ${values.length === 0 ? 'selected' : ''}`}
                        onClick={() => handleToggle('')}
                    >
                        <input
                            type="checkbox"
                            className="multiselect-checkbox"
                            checked={values.length === 0}
                            readOnly
                        />
                        {todosOption.label}
                    </div>
                )}
                {filteredOptions.length === 0 ? (
                    <div className="select-option-empty">Nenhuma opção encontrada</div>
                ) : (
                    filteredOptions.map(option => (
                        <div
                            key={option.value}
                            className={`select-option multiselect-option ${values.includes(option.value) ? 'selected' : ''}`}
                            onClick={() => handleToggle(option.value)}
                        >
                            <input
                                type="checkbox"
                                className="multiselect-checkbox"
                                checked={values.includes(option.value)}
                                readOnly
                            />
                            {option.label}
                        </div>
                    ))
                )}
            </div>
        </div>
    ) : null;

    return (
        <div className="select-container">
            {label && <label className="select-label">{label}</label>}
            <div className="select-wrapper" ref={wrapperRef}>
                <div
                    className={`select-input-container multiselect-input-container ${disabled ? 'disabled' : ''}`}
                    ref={inputContainerRef}
                    onClick={toggleDropdown}
                >
                    <div className="multiselect-display">
                        {displayContent}
                    </div>
                    {values.length > 0 && !disabled && (
                        <button
                            className="multiselect-clear"
                            onClick={(e) => { e.stopPropagation(); onChange([]); }}
                            tabIndex={-1}
                            aria-label="Limpar todos"
                            title="Limpar todos"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    )}
                    <FontAwesomeIcon
                        icon={faChevronDown}
                        className={`select-icon ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
                    />
                </div>
                {dropdown && ReactDOM.createPortal(dropdown, document.body)}
            </div>
        </div>
    );
};

export default MultiSelect;
